import * as React from 'react';
import { AlertTriangle, Check, PenLine, Sparkles, Undo2 } from 'lucide-react';
import { CitationChip } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Spinner } from '@/components/ui/spinner';
import { Callout, WorkspaceButton } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import { BLOCK_KIND_LABEL, EDIT_HINT } from './block-forms';
import { BlockBody } from './block-text';
import { RewritePanel } from './rewrite-panel';
import { TokenTextarea } from './token-textarea';
import { unknownTokens, type TokenValues } from './tokens';
import { type EditorBlock, type EditorCitation, type ReportEditorCalls } from './types';

/**
 * One block of a report (E14-design §5): read, edited by hand, or
 * rewritten by asking. In reading form the figures are chips, the
 * citations are citation chips, and an edited or rewritten block carries
 * its provenance chip. Hovering or focusing it shows Edit and Rewrite; an
 * edited block also has Back to AI draft.
 */

export type BlockMode = 'read' | 'edit' | 'rewrite';

export interface ReportBlockProps {
  block: EditorBlock;
  tokens: TokenValues;
  tokenSource?: string;
  readOnly?: boolean;
  focused?: boolean;
  /** The block Tab reaches (one per editor: the focused one, else the first). */
  tabbable?: boolean;
  mode: BlockMode;
  calls: ReportEditorCalls;
  onMode: (mode: BlockMode) => void;
  onFocus?: () => void;
  onOpenCitation?: (citation: EditorCitation) => void;
}

function isEdited(block: EditorBlock): boolean {
  return block.origin === 'consultant_edited' || block.origin === 'ai_rewritten';
}

export const ReportBlock = React.forwardRef<HTMLDivElement, ReportBlockProps>(function ReportBlock(
  {
    block,
    tokens,
    tokenSource,
    readOnly = false,
    focused = false,
    tabbable = false,
    mode,
    calls,
    onMode,
    onFocus,
    onOpenCitation,
  },
  ref,
) {
  const [draft, setDraft] = React.useState(block.text);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);
  const [confirmRevert, setConfirmRevert] = React.useState(false);
  // Results that land after unmount are dropped. Set on mount too: a
  // development StrictMode remount runs the cleanup once in between.
  const live = React.useRef(true);
  React.useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // Editing starts from what the block says now, not what it said at mount.
  React.useEffect(() => {
    if (mode === 'edit') setDraft(block.text);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const unknown = mode === 'edit' ? unknownTokens(draft, tokens) : [];
  const editable = !readOnly;

  const run = async (call: () => Promise<unknown>, after?: () => void) => {
    setSaving(true);
    setError(null);
    try {
      await call();
      if (live.current) after?.();
    } catch (err) {
      if (live.current) setError(err);
    } finally {
      if (live.current) setSaving(false);
    }
  };

  const save = () =>
    run(
      () => calls.onSave(block.id, { text: draft, origin: 'consultant_edited' }),
      () => onMode('read'),
    );

  const revert = () =>
    run(
      () => calls.onRevert(block.id),
      () => setConfirmRevert(false),
    );

  const edited = isEdited(block);
  const hintId = `block-hint-${block.id}`;

  return (
    <div
      ref={ref}
      tabIndex={tabbable ? 0 : -1}
      data-block-id={block.id}
      data-focused={focused ? 'true' : undefined}
      aria-label={`${BLOCK_KIND_LABEL[block.kind]}${edited ? ', edited' : ''}`}
      onFocus={onFocus}
      className={cn(
        'group relative flex flex-col gap-2 rounded-card border px-3 py-2.5 outline-none transition-colors',
        focused ? 'border-ink-200 bg-paper-50' : 'border-transparent hover:border-ink-100',
        'focus-visible:ring-2 focus-visible:ring-accent-amber-600',
      )}
    >
      {block.flag ? (
        <Callout tone="warn" icon={AlertTriangle}>
          {block.flag}
        </Callout>
      ) : null}

      {mode === 'edit' ? (
        <div className="flex flex-col gap-2">
          <label htmlFor={`block-edit-${block.id}`} className="sr-only">
            {block.kind === 'bullets' ? 'List, one item per line' : BLOCK_KIND_LABEL[block.kind]}
          </label>
          <TokenTextarea
            id={`block-edit-${block.id}`}
            aria-describedby={hintId}
            autoFocus
            value={draft}
            disabled={saving}
            onChange={setDraft}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onMode('read');
              } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (!saving && unknown.length === 0 && draft !== block.text) void save();
              }
            }}
          />
          <p id={hintId} className="m-0 text-meta text-ink-500">
            {EDIT_HINT[block.kind]}
            Figures keep their {'{{…}}'} names and the report fills them in: move or delete one as a
            whole, but it can&rsquo;t be typed over. Do not type numbers.
          </p>
          {unknown.length > 0 ? (
            <Callout tone="warn" icon={AlertTriangle}>
              {unknown.join(', ')} {unknown.length === 1 ? 'is not a figure' : 'are not figures'} in
              this report. Remove {unknown.length === 1 ? 'it' : 'them'} to save.
            </Callout>
          ) : null}
          {error ? <ErrorMessage error={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceButton
              variant="primary"
              disabled={saving || unknown.length > 0 || draft === block.text}
              onClick={() => void save()}
            >
              {saving ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Save
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" disabled={saving} onClick={() => onMode('read')}>
              Cancel
            </WorkspaceButton>
          </div>
        </div>
      ) : (
        <BlockBody
          kind={block.kind}
          text={block.text}
          tokens={tokens}
          source={tokenSource}
          className="text-ink-900"
          block={block}
        />
      )}

      {mode !== 'edit' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {block.citations.length > 0 ? (
            <ul className="m-0 flex min-w-0 list-none flex-wrap gap-1.5 p-0">
              {block.citations.map((c) => (
                <li key={c.id} className="min-w-0">
                  <CitationChip
                    cite={{
                      id: c.id,
                      doc_label: c.label,
                      confidence: c.confidence ?? 1,
                    }}
                    showConfidence={c.confidence !== undefined}
                    onOpen={() => onOpenCitation?.(c)}
                  />
                </li>
              ))}
            </ul>
          ) : null}
          {edited ? (
            <ProvenanceChip
              origin={block.origin === 'ai_rewritten' ? 'ai_rewritten' : 'consultant_edited'}
              lineage={{
                confirmed_by: block.editedBy ?? undefined,
                confirmed_at: block.editedAt ?? undefined,
                was: 'ai_proposed',
              }}
            />
          ) : null}
          {block.origin === 'ai_rewritten' && block.lastInstruction ? (
            <span className="text-meta text-ink-500">“{block.lastInstruction}”</span>
          ) : null}
          {editable && mode === 'read' ? (
            <div
              className={cn(
                'ml-auto flex items-center gap-1 transition-opacity',
                focused || confirmRevert
                  ? 'opacity-100'
                  : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
              )}
            >
              <WorkspaceButton variant="tertiary" disabled={saving} onClick={() => onMode('edit')}>
                <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                Edit
              </WorkspaceButton>
              <WorkspaceButton
                variant="tertiary"
                disabled={saving}
                onClick={() => onMode('rewrite')}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Rewrite
              </WorkspaceButton>
              {edited && !confirmRevert ? (
                <WorkspaceButton
                  variant="tertiary"
                  disabled={saving}
                  onClick={() => setConfirmRevert(true)}
                >
                  <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Back to AI draft
                </WorkspaceButton>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {confirmRevert && mode === 'read' ? (
        <Callout tone="ask" icon={Undo2}>
          Put the AI&rsquo;s draft back in place of this text? The current text stays in the
          report&rsquo;s audit history.
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <WorkspaceButton variant="primary" disabled={saving} onClick={() => void revert()}>
              {saving ? <Spinner className="h-3.5 w-3.5" /> : null}
              Back to AI draft
            </WorkspaceButton>
            <WorkspaceButton
              variant="tertiary"
              disabled={saving}
              onClick={() => setConfirmRevert(false)}
            >
              Keep this text
            </WorkspaceButton>
          </div>
        </Callout>
      ) : null}
      {error && mode !== 'edit' ? <ErrorMessage error={error} /> : null}

      {mode === 'rewrite' && editable ? (
        <RewritePanel
          block={block}
          tokens={tokens}
          tokenSource={tokenSource}
          onRewrite={(request) => calls.onRewrite(block.id, request)}
          onAccept={async (proposal, instruction) => {
            await calls.onSave(block.id, {
              text: proposal.text,
              origin: 'ai_rewritten',
              instruction,
              citations: proposal.citations.map((c) => c.id),
            });
            onMode('read');
          }}
          onClose={() => onMode('read')}
          onEditByHand={() => onMode('edit')}
          onOpenCitation={onOpenCitation}
        />
      ) : null}
    </div>
  );
});
